import * as io from "io-ts";
import t from "../../../../../i18n";
import { literalUnion, nullable } from "diagnostics-lib/io";
import { filterData, NONE_LABEL } from "@ui/Filter";
import { getQuantityTypeLabel } from "./getQuantityType";
import type { FilterOption } from "@ui/Filter/FilterSelect";
import { type Quantity, type QuantityRole, QUANTITY_ROLES } from "../../../../../api/useQuantities";

const typeUnion = literalUnion(["MEASURE", "FORMULA"]);
const typeIndicators = literalUnion(["performance", "process"]);

export type QuantityWithRole = Quantity & {
  roles: Set<QuantityRole>;
};
export type EnhancedQuantity = QuantityWithRole & {
  filters: ("activation" | "metrology")[];
  isPromoted: boolean;
};
type QuantityIndicators = io.TypeOf<typeof typeIndicators>;

export const QuantityFilteredValues = io.strict({
  unit: io.array(nullable(io.string)),
  type: io.array(typeUnion),
  role: io.array(literalUnion(QUANTITY_ROLES)),
  system: io.array(nullable(io.string)),
  indicators: io.array(typeIndicators),
  filters: io.array(io.union([io.literal("activation"), io.literal("metrology")])),
});
type QuantityFilteredValues = io.TypeOf<typeof QuantityFilteredValues>;

export const DEFAULT_QUANTITY_FILTER_VALUES: QuantityFilteredValues = {
  unit: [],
  type: [],
  role: [],
  system: [],
  indicators: [],
  filters: [],
};

export const QUANTITY_FILTERS_METADATA = {
  type: {
    title: t("commons.quantities.selectFilter.type"),
    getOptions: (quantity: EnhancedQuantity) => ({
      label: getQuantityTypeLabel(quantity.type),
      value: quantity.type,
    }),
    hidden: false,
  },
  unit: {
    title: t("commons.quantities.selectFilter.unit"),
    getOptions: (quantity: EnhancedQuantity) => ({
      label: quantity.unit ?? NONE_LABEL,
      value: quantity.unit,
    }),
    withNone: true,
    hidden: false,
  },
  role: {
    title: t("commons.quantities.selectFilter.role"),
    getOptions: (quantity: EnhancedQuantity) =>
      Array.from(quantity.roles).map(role => ({ label: getRoleLabel(role), value: role })),
    sortBy: (o: FilterOption<string | null>) => getRoleWeight(o.value as QuantityRole).toString(),
    showTag: true,
    hidden: false,
  },
  system: {
    title: t("commons.quantities.selectFilter.system"),
    getOptions: (quantity: Quantity) => ({
      label: quantity.systemId ?? NONE_LABEL,
      value: quantity.systemId ?? null,
    }),
    withNone: true,
  },
  indicators: {
    title: t("commons.quantities.selectFilter.indicator"),
    getOptions: (quantity: EnhancedQuantity) => {
      const indicators = [];
      if (quantity.qoiOrder) {
        indicators.push({
          label: getIndicatorsLabel("performance"),
          value: "performance",
        });
      }
      if (quantity.processIndicatorOrder) {
        indicators.push({
          label: getIndicatorsLabel("process"),
          value: "process",
        });
      }
      return [...indicators] as { label: string; value: QuantityIndicators }[];
    },
    hidden: false,
  },
  filters: {
    title: t("commons.quantities.selectFilter.filters"),
    getOptions: (quantity: EnhancedQuantity) =>
      quantity.filters.map(f => ({
        label:
          f === "activation"
            ? t("commons.quantities.selectFilter.filters.activation")
            : t("commons.quantities.selectFilter.filters.metrology"),
        value: f,
      })),
    hidden: false,
  },
};

export const getQuantitiesFilterMetadata = (showRoleFilter: boolean, hideUnitFilter: boolean) => {
  const metadata = { ...QUANTITY_FILTERS_METADATA };
  metadata.role["hidden"] = !showRoleFilter;
  metadata.unit["hidden"] = hideUnitFilter;
  return metadata;
};

export const filterQuantities = filterData<
  EnhancedQuantity,
  keyof typeof QUANTITY_FILTERS_METADATA,
  typeof QUANTITY_FILTERS_METADATA
>(QUANTITY_FILTERS_METADATA);

export function getRoleLabel(role: QuantityRole) {
  switch (role) {
    case "simulationInput":
      return t("commons.quantities.selectTypeOption.simulationInput");
    case "observable":
      return t("commons.quantities.selectTypeOption.observable");
    case "simulationOutput":
      return t("commons.quantities.selectTypeOption.simulationOutput");
    case "others":
      return t("commons.quantities.selectTypeOption.others");
    default:
      throw new Error(role satisfies never);
  }
}
export const getRoleWeight = (role: QuantityRole) => {
  switch (role) {
    case "observable":
      return 300;
    case "simulationOutput":
      return 200;
    case "simulationInput":
      return 100;
    case "others":
      return 0;
    default:
      throw new Error(role satisfies never);
  }
};

export const getIndicatorsLabel = (indicator: QuantityIndicators) => {
  switch (indicator) {
    case "performance":
      return t("commons.quantities.selectTypeOption.indicator.performance");
    case "process":
      return t("commons.quantities.selectTypeOption.indicator.process");
    default:
      throw new Error(indicator satisfies never);
  }
};
